import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderRadiusProps,
  FlexProps,
  PositionProps,
  SizeProps,
} from 'styled-system';
import { backgroundColor, borderRadius, position, size, space, width } from 'styled-system';

import { useLocalization } from 'common/providers/LocaleProvider';
import type { FillProps } from 'common/theme/system-types';
import type {
  ThemeBottomProps,
  ThemeLeftProps,
  ThemeRightProps,
  ThemeTopProps,
} from '../../theme/system-utilities';
import { fill, themeBottom, themeLeft, themeRight, themeTop } from '../../theme/system-utilities';
import { Flex } from '../Flex';
import type { IconPropsWithName } from '../Icon';
import Icon from '../Icon';
import { Text } from '../Text';

const ICON_SIZE = 32;

interface DefaultSquareProductImageProps {
  title: string;
  href?: string;
  onClick?: () => void;
}

const UnstyledFallBackText = styled(Text)``;

const FallBackText = (props: ComponentProps<typeof UnstyledFallBackText>) => (
  <UnstyledFallBackText
    lineHeight="lh_24"
    fontSize="fs_16"
    color="sys.neutral.text.default"
    textAlign="center"
    {...props}
  />
);

const UnstyledDefaultImageBackground = styled(Flex)<
  FlexProps & BackgroundColorProps & BorderRadiusProps & PositionProps
>`
  ${backgroundColor};
  ${borderRadius}
  ${position}
  ${size};
`;

const DefaultImageBackground = (props: ComponentProps<typeof UnstyledDefaultImageBackground>) => (
  <UnstyledDefaultImageBackground
    bg="sys.neutral.background.weak"
    borderRadius="medium"
    position="relative"
    pt="100%"
    width={1}
    {...props}
  />
);

const UnstyledDefaultImage = styled(Icon)<IconPropsWithName & SizeProps & FillProps>`
  ${position};
  ${width};
  ${fill};
  ${size};
`;

const DefaultImage = (props: ComponentProps<typeof UnstyledDefaultImage>) => (
  <UnstyledDefaultImage role="img" size={ICON_SIZE} fill="sys.neutral.icon.default" {...props} />
);

const UnstyledContentContainer = styled(Flex)<
  FlexProps & PositionProps & ThemeTopProps & ThemeBottomProps & ThemeLeftProps & ThemeRightProps
>`
  ${position};
  ${space};
  ${themeTop};
  ${themeBottom};
  ${themeRight};
  ${themeLeft};
  ${width};
`;

const ContentContainer = (props: ComponentProps<typeof UnstyledContentContainer>) => (
  <UnstyledContentContainer
    position="absolute"
    themeTop="sp_0"
    themeBottom="sp_0"
    themeLeft="sp_0"
    themeRight="sp_0"
    margin="auto"
    alignItems="center"
    justifyContent="center"
    flexDirection="column"
    {...props}
  />
);

const DefaultSquareProductImage = ({ title, href }: DefaultSquareProductImageProps) => {
  const { t } = useLocalization();

  return (
    <DefaultImageBackground
      data-testid="default-square-product-image"
      title={title}
      aria-label={title}
      {...(href ? { as: 'a', href } : {})}
    >
      <ContentContainer>
        <DefaultImage name="defaultProductImage" ariaLabel={title} />
        <FallBackText>{t('h24_no_picture_available')}</FallBackText>
      </ContentContainer>
    </DefaultImageBackground>
  );
};

export default DefaultSquareProductImage;
